// Procedural pixel overlay generation for the life layer
// Draws sparse pixel-art details into a chunk canvas

import { createRng } from './rng.mjs';
import { TILE_COLORS } from '../../gdd-types/tileTypes.mjs';

/**
 * Draw life details for a single tile onto a chunk canvas context.
 *
 * @param {CanvasRenderingContext2D} ctx - chunk offscreen canvas context
 * @param {string} tileType - the terrain type to draw life for
 * @param {number} ox - x offset of tile center within chunk canvas
 * @param {number} oy - y offset of tile center within chunk canvas
 * @param {number} hexSize - hex size in world pixels
 * @param {number} seed - deterministic seed for this tile
 * @param {number} ageSec - seconds since tile was placed
 * @param {object} rng - seeded RNG instance
 */
export function drawLifeForTile(ctx, tileType, ox, oy, hexSize, seed, ageSec, rng) {
  const r = hexSize * 0.8; // stay inside hex

  switch (tileType) {
    case 'ocean':
      drawOceanLife(ctx, ox, oy, r, rng, ageSec);
      break;
    case 'grass':
      drawGrassLife(ctx, ox, oy, r, rng);
      break;
    case 'grassland':
      drawGrasslandLife(ctx, ox, oy, r, rng);
      break;
    case 'mountain':
      drawMountainLife(ctx, ox, oy, r, rng);
      break;
    case 'snow':
      drawSnowLife(ctx, ox, oy, r, rng);
      break;
    case 'desert':
      drawDesertLife(ctx, ox, oy, r, rng);
      break;
    case 'deadplains':
      drawDeadplainsLife(ctx, ox, oy, r, rng);
      break;
  }
}

function drawOceanLife(ctx, ox, oy, r, rng, ageSec) {
  // Foam stipples
  const count = 12 + Math.floor(rng.nextFloat() * 8);
  ctx.fillStyle = 'rgba(255,255,255,0.25)';
  for (let i = 0; i < count; i++) {
    const angle = rng.nextFloat() * Math.PI * 2;
    const dist = (0.4 + rng.nextFloat() * 0.6) * r;
    const px = ox + Math.cos(angle) * dist;
    const py = oy + Math.sin(angle) * dist;
    ctx.fillRect(Math.floor(px), Math.floor(py), 1, 1);
  }
  // Wave bands (subtle horizontal lines)
  ctx.strokeStyle = 'rgba(200,230,255,0.15)';
  ctx.lineWidth = 1;
  const waveOffset = (ageSec * 2) % 10;
  for (let i = -3; i < 4; i++) {
    const y = oy + i * (r * 0.25) + waveOffset;
    ctx.beginPath();
    ctx.moveTo(ox - r * 0.5, y);
    ctx.lineTo(ox + r * 0.5, y);
    ctx.stroke();
  }
}

function drawGrassLife(ctx, ox, oy, r, rng) {
  // Stipple dots
  const count = 15 + Math.floor(rng.nextFloat() * 10);
  for (let i = 0; i < count; i++) {
    const angle = rng.nextFloat() * Math.PI * 2;
    const dist = rng.nextFloat() * r;
    const px = ox + Math.cos(angle) * dist;
    const py = oy + Math.sin(angle) * dist;
    const shade = rng.nextFloat() > 0.7 ? 'rgba(255,220,50,0.4)' : 'rgba(50,180,50,0.3)';
    ctx.fillStyle = shade;
    ctx.fillRect(Math.floor(px), Math.floor(py), 1, 1);
  }
  // Occasional tuft clusters (small cross shapes)
  const tufts = 2 + Math.floor(rng.nextFloat() * 3);
  ctx.fillStyle = 'rgba(30,150,30,0.5)';
  for (let i = 0; i < tufts; i++) {
    const angle = rng.nextFloat() * Math.PI * 2;
    const dist = rng.nextFloat() * r * 0.7;
    const px = Math.floor(ox + Math.cos(angle) * dist);
    const py = Math.floor(oy + Math.sin(angle) * dist);
    ctx.fillRect(px, py - 1, 1, 3);
    ctx.fillRect(px - 1, py, 3, 1);
  }
}

function drawGrasslandLife(ctx, ox, oy, r, rng) {
  const count = 12 + Math.floor(rng.nextFloat() * 8);
  for (let i = 0; i < count; i++) {
    const angle = rng.nextFloat() * Math.PI * 2;
    const dist = rng.nextFloat() * r;
    const px = ox + Math.cos(angle) * dist;
    const py = oy + Math.sin(angle) * dist;
    const shade = rng.nextFloat() > 0.6 ? 'rgba(180,200,60,0.35)' : 'rgba(120,180,50,0.25)';
    ctx.fillStyle = shade;
    ctx.fillRect(Math.floor(px), Math.floor(py), 1, 1);
  }
}

function drawMountainLife(ctx, ox, oy, r, rng) {
  const count = 20 + Math.floor(rng.nextFloat() * 10);
  for (let i = 0; i < count; i++) {
    const angle = rng.nextFloat() * Math.PI * 2;
    const dist = rng.nextFloat() * r;
    const px = ox + Math.cos(angle) * dist;
    const py = oy + Math.sin(angle) * dist;
    const shade = rng.nextFloat() > 0.5 ? 'rgba(100,80,40,0.3)' : 'rgba(60,50,30,0.3)';
    ctx.fillStyle = shade;
    ctx.fillRect(Math.floor(px), Math.floor(py), 1, 1);
  }
}

function drawSnowLife(ctx, ox, oy, r, rng) {
  const count = 18 + Math.floor(rng.nextFloat() * 8);
  for (let i = 0; i < count; i++) {
    const angle = rng.nextFloat() * Math.PI * 2;
    const dist = rng.nextFloat() * r;
    const px = ox + Math.cos(angle) * dist;
    const py = oy + Math.sin(angle) * dist;
    ctx.fillStyle = 'rgba(200,220,255,0.3)';
    ctx.fillRect(Math.floor(px), Math.floor(py), 1, 1);
  }
}

function drawDesertLife(ctx, ox, oy, r, rng) {
  const count = 10 + Math.floor(rng.nextFloat() * 8);
  for (let i = 0; i < count; i++) {
    const angle = rng.nextFloat() * Math.PI * 2;
    const dist = rng.nextFloat() * r;
    const px = ox + Math.cos(angle) * dist;
    const py = oy + Math.sin(angle) * dist;
    ctx.fillStyle = 'rgba(200,170,80,0.3)';
    ctx.fillRect(Math.floor(px), Math.floor(py), 1, 1);
  }
}

function drawDeadplainsLife(ctx, ox, oy, r, rng) {
  const count = 10 + Math.floor(rng.nextFloat() * 6);
  for (let i = 0; i < count; i++) {
    const angle = rng.nextFloat() * Math.PI * 2;
    const dist = rng.nextFloat() * r;
    const px = ox + Math.cos(angle) * dist;
    const py = oy + Math.sin(angle) * dist;
    ctx.fillStyle = rng.nextFloat() > 0.6
      ? 'rgba(90,70,50,0.3)'
      : 'rgba(60,50,40,0.25)';
    ctx.fillRect(Math.floor(px), Math.floor(py), 1, 1);
  }
}
